// AR calibration: the mode machine and the sanity limits (2026-10-05,
// Christchurch field test). Pure - no DOM, no imports - so it runs under
// plain Node (frontend/tests/arCalState.test.ts, driven by
// tests/test_field_ar_calibration_ui.py).
//
// What went wrong on the phone:
//  * "a bar through all of my buttons after trying to calibrate": the empty
//    calibration sheet stayed on screen. `.fa-ar-sheet { display: flex }`
//    beat the `hidden` attribute, so the closed (empty) sheet was a white
//    padded strip at bottom: 4.6rem - across the control chips once they
//    wrapped to two rows ("Recalibrate ▾" is wider than "Calibrate ▾").
//    Fixed in field.css; and ar.ts now derives every overlay (crosshair,
//    road guides, sheet, preview offset) from the mode via overlaysFor(),
//    so leaving a mode by apply, cancel or "Off" always clears them.
//  * "I clicked the wrong asset... now the whole map is in the wrong spot":
//    a pick was applied at once, with no preview, no undo and no check that
//    the implied move was plausible. Now: preview -> Apply / Cancel, undo,
//    and calibrationSanity() below.

export type ArMode = "view" | "cal-tap" | "cal-pick" | "cal-preview" | "road" | "road-manual" | "kerb";

export type ArAction =
  | "menu-tap" // "Tap a lid or meter I can see"
  | "menu-road" // "Line up with the road": camera-assisted (the server finds the kerb in a frame)
  | "manual-guides" // ... or by eye with the vanishing-point guides
  | "menu-kerb" // "Tap two points on the kerb" (2026-10-05)
  | "tapped" // a calibration tap: show the candidates
  | "picked" // a candidate chosen: preview the shifted overlay
  | "heading-result" // road / camera auto-align answered: preview it
  | "direct" // 2026-10-06: hold-and-drag / two-finger twist on the overlay: preview it
  | "tap-again" // back to tapping
  | "apply"
  | "cancel"
  | "stop"; // the AR view closed / "Off"

export function nextMode(mode: ArMode, action: ArAction): ArMode {
  switch (action) {
    case "menu-tap":
    case "tap-again":
      return "cal-tap";
    case "menu-road":
      return "road";
    case "manual-guides":
      return mode === "road" || mode === "cal-preview" ? "road-manual" : mode;
    case "menu-kerb":
      return "kerb";
    case "tapped":
      // a kerb tap stays in the kerb mode (the second tap asks the server)
      if (mode === "kerb") return "kerb";
      // from the preview too: "Pick another" goes back to the same list
      return mode === "cal-tap" || mode === "cal-pick" || mode === "cal-preview" ? "cal-pick" : mode;
    case "picked":
      return mode === "cal-pick" ? "cal-preview" : mode;
    case "heading-result":
      return "cal-preview";
    case "direct":
      // only from the plain view or an earlier direct preview (a second point,
      // a twist after a drag); never in the middle of another calibration
      return mode === "view" || mode === "cal-preview" ? "cal-preview" : mode;
    case "apply":
    case "cancel":
    case "stop":
      return "view";
  }
}

export interface ArOverlays {
  /** the yellow tap crosshair in the middle of the camera view */
  crosshair: boolean;
  /** the yellow manual "line up with the road" guides (vanishing point + ground fan) */
  roadGuides: boolean;
  /** the kerb taps placed so far (and the line through them) */
  kerbTaps: boolean;
  /** the bottom sheet: "never" = closed and emptied, "required" = this
   * mode's content (candidates / preview), "optional" = only an identify
   * card or a message the user opened */
  sheet: "never" | "optional" | "required";
  /** draw with the calibration being previewed instead of the active one */
  previewCal: boolean;
  /** which control set the bottom panel shows */
  panel: "main" | "cal-tap" | "road" | "road-manual" | "kerb" | "preview";
}

export function overlaysFor(mode: ArMode): ArOverlays {
  switch (mode) {
    case "view":
      return { crosshair: false, roadGuides: false, kerbTaps: false, sheet: "optional", previewCal: false, panel: "main" };
    case "cal-tap":
      return { crosshair: true, roadGuides: false, kerbTaps: false, sheet: "never", previewCal: false, panel: "cal-tap" };
    case "cal-pick":
      return { crosshair: false, roadGuides: false, kerbTaps: false, sheet: "required", previewCal: false, panel: "cal-tap" };
    case "cal-preview":
      return { crosshair: false, roadGuides: false, kerbTaps: false, sheet: "required", previewCal: true, panel: "preview" };
    case "road":
      return { crosshair: false, roadGuides: false, kerbTaps: false, sheet: "never", previewCal: false, panel: "road" };
    case "road-manual":
      return { crosshair: false, roadGuides: true, kerbTaps: false, sheet: "never", previewCal: false, panel: "road-manual" };
    case "kerb":
      return { crosshair: false, roadGuides: false, kerbTaps: true, sheet: "never", previewCal: false, panel: "kerb" };
  }
}

// --- sanity limits -------------------------------------------------------
// Mirrors field_ar.calibration_sanity (tests compare the two on the same
// cases). A lid calibration may move the overlay as far as GPS can
// plausibly be out (3 x the reported accuracy, at least 8 m) and turn it as
// far as a phone compass plausibly errs (25 deg). Beyond that it is far more
// likely the wrong asset was picked - warn and make "Apply anyway" a
// deliberate second choice. Beyond twice that (or 45 deg, which the server
// already refuses) refuse outright.

export const CAL_WARN_HEADING_DEG = 25;
export const CAL_REFUSE_HEADING_DEG = 45;
export const CAL_MIN_SHIFT_LIMIT_M = 8;
export const CAL_SHIFT_PER_ACCURACY = 3;
/** two candidates closer than this are shown as an ambiguous pair */
export const CAL_CLOSE_PAIR_M = 2.5;

export interface CalSanity {
  level: "ok" | "warn" | "refuse";
  /** how far the picked asset moves on screen-ground: from its mapped
   * position (raw GPS + compass) to where the tap hit the ground */
  move_m: number;
  heading_deg: number;
  limit_m: number;
  message: string | null;
}

function wrap180(d: number): number {
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

/** `assetE/N`: the picked asset relative to the RAW fix (metres east /
 * north). `tapAzimuth` / `tapRange`: the tapped ray in the raw pose (range
 * null when the ray doesn't meet the ground usably). `headingOffset`: the
 * calibration's compass correction. */
export function calibrationSanity(
  assetE: number,
  assetN: number,
  tapAzimuth: number,
  tapRange: number | null,
  headingOffset: number,
  gpsAccuracy: number | null,
  mode: "position" | "full" = "full",
): CalSanity {
  const dist = Math.hypot(assetE, assetN);
  const dh = Math.abs(wrap180(headingOffset));
  let move: number;
  const a = (tapAzimuth * Math.PI) / 180;
  if (tapRange !== null && Number.isFinite(tapRange) && tapRange > 0) {
    move = Math.hypot(assetE - tapRange * Math.sin(a), assetN - tapRange * Math.cos(a));
  } else if (mode === "position") {
    // position only, no ground range: the smallest move putting the asset on the tapped ray
    const along = assetE * Math.sin(a) + assetN * Math.cos(a);
    move = Math.hypot(assetE - along * Math.sin(a), assetN - along * Math.cos(a));
  } else {
    // no ground range: the asset swings round by the heading change
    move = 2 * dist * Math.sin(((dh / 2) * Math.PI) / 180);
  }
  const acc = gpsAccuracy !== null && Number.isFinite(gpsAccuracy) && gpsAccuracy > 0 ? gpsAccuracy : 8;
  const limit = Math.max(CAL_SHIFT_PER_ACCURACY * acc, CAL_MIN_SHIFT_LIMIT_M);
  const r1 = (x: number) => Math.round(x * 10) / 10;
  const out: CalSanity = { level: "ok", move_m: r1(move), heading_deg: r1(dh), limit_m: r1(limit), message: null };
  if (move > 2 * limit || dh > CAL_REFUSE_HEADING_DEG) {
    out.level = "refuse";
    out.message = `That would move everything ${Math.round(move)} m${dh >= 1 ? ` and turn it ${Math.round(dh)}°` : ""} — far more than GPS (±${Math.round(acc)} m) and the compass are ever out. It's almost certainly the wrong asset: pick another or tap again.`;
  } else if (move > limit || dh > CAL_WARN_HEADING_DEG) {
    out.level = "warn";
    out.message = `That would move everything ${Math.round(move)} m${dh >= 1 ? ` and turn it ${Math.round(dh)}°` : ""} — more than GPS (±${Math.round(acc)} m) and the compass are usually out. Probably the wrong asset.`;
  }
  return out;
}

/** Heading-only corrections (road / camera auto-align). */
export function headingSanity(headingOffset: number): CalSanity {
  const dh = Math.abs(wrap180(headingOffset));
  const r1 = (x: number) => Math.round(x * 10) / 10;
  if (dh > CAL_REFUSE_HEADING_DEG) {
    return { level: "refuse", move_m: 0, heading_deg: r1(dh), limit_m: 0,
      message: `That would turn everything ${Math.round(dh)}° — no compass is that far out. Probably the wrong road.` };
  }
  if (dh > CAL_WARN_HEADING_DEG) {
    return { level: "warn", move_m: 0, heading_deg: r1(dh), limit_m: 0,
      message: `That would turn everything ${Math.round(dh)}° — more than a compass is usually out. Check you're facing along the right street.` };
  }
  return { level: "ok", move_m: 0, heading_deg: r1(dh), limit_m: 0, message: null };
}

/** Kerb calibration (heading + a shift across the road): the shift is
 * judged like a lid calibration's move, the turn like a road's. */
export function kerbSanity(shiftM: number, headingOffset: number, gpsAccuracy: number | null): CalSanity {
  const h = headingSanity(headingOffset);
  const acc = gpsAccuracy !== null && Number.isFinite(gpsAccuracy) && gpsAccuracy > 0 ? gpsAccuracy : 8;
  const limit = Math.max(CAL_SHIFT_PER_ACCURACY * acc, CAL_MIN_SHIFT_LIMIT_M);
  const move = Math.abs(shiftM);
  const r1 = (x: number) => Math.round(x * 10) / 10;
  const out: CalSanity = { level: h.level, move_m: r1(move), heading_deg: h.heading_deg, limit_m: r1(limit), message: h.message };
  if (move > 2 * limit) {
    out.level = "refuse";
    out.message = `That would move you ${Math.round(move)} m across the road — far more than GPS (±${Math.round(acc)} m) is ever out. Check you tapped the near kerb of the street you're on.`;
  } else if (move > limit && out.level === "ok") {
    out.level = "warn";
    out.message = `That would move you ${Math.round(move)} m across the road — more than GPS (±${Math.round(acc)} m) is usually out. Check you tapped the near kerb.`;
  }
  return out;
}

/** Candidates (metres east / north) within CAL_CLOSE_PAIR_M of another:
 * index -> the indexes of its close neighbours. */
export function closePairs(points: { e: number; n: number }[], within = CAL_CLOSE_PAIR_M): Map<number, number[]> {
  const out = new Map<number, number[]>();
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      if (Math.hypot(points[i].e - points[j].e, points[i].n - points[j].n) <= within) {
        out.set(i, [...(out.get(i) ?? []), j]);
        out.set(j, [...(out.get(j) ?? []), i]);
      }
    }
  }
  return out;
}

// --- lid calibrations: position only by default (2026-10-05) -----------------
//
// iPhone report: "calibrating with a hydrant: position is corrected but the
// orientation goes all over the show ... It would be good to calibrate
// position only when tapping a lid, as that's usually what people want.
// Road direction is better for orientation." A lid tap now sets the
// position anchor only; the direction stays with the compass, the gyro and
// any road / kerb alignment, unless "Also correct direction" is ticked.

export type CalMode = "position" | "full";

export interface ActiveDirection {
  heading_offset_deg: number;
  /** where that direction came from ("road (Washbournes Road)", "kerb (Washbournes Road)", "hydrant") */
  heading_from?: string | null;
}

/** The heading part of a lid calibration: `mode` "position" keeps the
 * active calibration's direction correction (a road alignment survives a
 * lid tap) or none; "full" takes the server's new offset from the lid. */
export function lidDirection(mode: CalMode, active: ActiveDirection | null, serverOffset: number, assetShort: string):
  { heading_offset_deg: number; heading_from: string } {
  if (mode === "full") return { heading_offset_deg: serverOffset, heading_from: assetShort };
  if (active && Math.abs(active.heading_offset_deg) >= 0.05) {
    return { heading_offset_deg: active.heading_offset_deg, heading_from: active.heading_from ?? "earlier calibration" };
  }
  return { heading_offset_deg: 0, heading_from: "compass" };
}

/** The preview card's first line for a lid calibration. */
export function lidPreviewText(mode: CalMode, east: number, north: number, headingOffset: number, headingFrom: string): string {
  const shift = Math.hypot(east, north);
  const pts = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const dir = pts[Math.round((((Math.atan2(east, north) * 180) / Math.PI + 360) % 360) / 45) % 8];
  const s = (v: number, d = 1) => `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(d)}`;
  const move = shift >= 0.05 ? `Moves your position ${shift.toFixed(1)} m ${dir} (${s(east)} m E, ${s(north)} m N)` : "Leaves your position as it is";
  if (mode === "full") return `${move} and turns the compass ${s(headingOffset, 0)}°.`;
  return `${move}. Direction unchanged: from the ${headingFrom}${headingFrom === "compass" ? "" : `, ${s(headingOffset, 0)}°`}.`;
}

/** The persistent "Calibrated: ..." line. A position-only lid calibration
 * (mode "position") that kept no direction correction says so instead of
 * "+0°". */
export function calibrationSummary(cal: { east_m: number; north_m: number; heading_offset_deg: number; at: number; mode?: string },
  now: number): string {
  const shift = Math.hypot(cal.east_m, cal.north_m);
  const hd = cal.heading_offset_deg;
  const mins = Math.max(0, Math.round((now - cal.at) / 60000));
  const parts = [];
  if (shift >= 0.05) parts.push(`shifted ${shift.toFixed(1)} m`);
  if (cal.mode === "position" && Math.abs(hd) < 0.5) parts.push("position only");
  else parts.push(`${hd >= 0 ? "+" : "−"}${Math.abs(hd).toFixed(0)}°`);
  parts.push(mins < 1 ? "just now" : `${mins} min ago`);
  return `Calibrated: ${parts.join(", ")}`;
}

// --- direct manipulation (2026-10-06) ----------------------------------------
//
// iPhone feedback: "maybe another way of calibrating would be to click and
// hold on the asset overlay and drag it, so I don't have to choose an asset
// to calibrate against from the list ... A rotary two-touch action could
// change the direction ... or calibrating a second point while the first is
// in view could change the alignment". The maths is arMath.solveRigid2D;
// these are its limits, mirrored in field_ar.direct_sanity.

export type DirectMode = "drag" | "rotate" | "two_point";

/** two points closer than this can't pin a direction */
export const TWO_POINT_MIN_SPAN_M = 2;

/** The verdict on a drag / twist / two-point correction: the position move
 * (|t|) is judged like a lid calibration's, the turn like a road's, and two
 * points that disagree (one dragged to the wrong spot, or the wrong asset)
 * by their residual: warn above max(1 m, 15 % of their spacing), refuse
 * above max(3 m, 50 %). */
export function directSanity(moveM: number, rotDeg: number, residualM: number | null, spanM: number | null,
  gpsAccuracy: number | null): CalSanity & { residual_m: number | null } {
  const h = headingSanity(rotDeg);
  const acc = gpsAccuracy !== null && Number.isFinite(gpsAccuracy) && gpsAccuracy > 0 ? gpsAccuracy : 8;
  const limit = Math.max(CAL_SHIFT_PER_ACCURACY * acc, CAL_MIN_SHIFT_LIMIT_M);
  const r1 = (x: number) => Math.round(x * 10) / 10;
  const out: CalSanity & { residual_m: number | null } = { level: h.level, move_m: r1(Math.abs(moveM)), heading_deg: h.heading_deg,
    limit_m: r1(limit), message: h.message, residual_m: residualM === null ? null : r1(residualM) };
  const worse = (level: "warn" | "refuse", message: string) => {
    if (level === "refuse" || out.level === "ok") {
      out.level = level;
      out.message = message;
    }
  };
  const move = Math.abs(moveM);
  if (move > 2 * limit) worse("refuse", `That would move you ${Math.round(move)} m — far more than GPS (±${Math.round(acc)} m) is ever out. Drag the overlay onto the thing it belongs to.`);
  else if (move > limit) worse("warn", `That would move you ${Math.round(move)} m — more than GPS (±${Math.round(acc)} m) is usually out. Is that the right asset?`);
  if (residualM !== null && spanM !== null && spanM > 0) {
    if (residualM > Math.max(3, 0.5 * spanM)) {
      worse("refuse", `The two points disagree by ${residualM.toFixed(1)} m — one of them is probably the wrong asset or spot. Drag them again.`);
    } else if (residualM > Math.max(1, 0.15 * spanM)) {
      worse("warn", `The two points disagree by ${residualM.toFixed(1)} m (the map, or one drag, is off). Check both sit where they should.`);
    }
  }
  return out;
}

/** "Undo" sits inline on the screen this long after a calibration or reset,
 * then tucks into the status sheet (where it stays for UNDO_MS). */
export const UNDO_INLINE_MS = 10_000;

export function undoInlineVisible(now: number, at: number | null, ms = UNDO_INLINE_MS): boolean {
  return at !== null && now >= at && now - at < ms;
}

export interface PillInput {
  /** a calibration is in force */
  calibrated: boolean;
  /** ... and it anchored the position (not heading-only) */
  posAnchor: boolean;
  /** anchorStatus().level */
  level: "held" | "gps" | "anchored" | "weak" | "drifting";
  /** 1-sigma metres of the tracked position */
  sigma: number;
  headingOffset: number;
  compass: "ok" | "disturbed" | "provisional" | "none";
  /** camera tracking, when switched on */
  vo: "good" | "weak" | "lost" | null;
}

export interface Pill {
  /** the position part: icon + text ("⚓ ±1.1 m", "GPS ±5 m", "held") */
  icon: "anchor" | "gps" | "hold" | "direction";
  text: string;
  /** colour of the pill */
  tone: "plain" | "amber" | "red";
  compass: PillInput["compass"];
  vo: PillInput["vo"];
  /** spoken label (aria-label) */
  label: string;
}

/** The small status pill (replaces the 6-line amber bar): anchor + accuracy,
 * a camera-tracking dot, the compass state. The detail is one tap away. */
export function statusPill(i: PillInput): Pill {
  const sig = `±${i.sigma < 9.5 ? i.sigma.toFixed(1) : Math.round(i.sigma)} m`;
  let icon: Pill["icon"] = "gps", text = `GPS ${sig}`, tone: Pill["tone"] = "plain";
  if (i.level === "held") {
    icon = "hold";
    text = `held ${sig}`;
    tone = "amber";
  } else if (i.calibrated && i.posAnchor) {
    icon = "anchor";
    text = sig;
    tone = i.level === "drifting" ? "red" : "amber";
  } else if (i.calibrated) {
    icon = "direction";
    text = `${i.headingOffset >= 0 ? "+" : "−"}${Math.abs(i.headingOffset).toFixed(0)}° · ${sig}`;
    tone = "amber";
  }
  if (i.compass === "disturbed" && tone !== "red") tone = "red";
  const words = [
    icon === "anchor" ? `Calibrated, position ${sig}${i.level === "weak" ? ", anchor fading" : i.level === "drifting" ? ", drifting: recalibrate" : ""}`
      : icon === "hold" ? `Position held, ${sig}` : icon === "direction" ? `Direction calibrated, position GPS ${sig}` : `Not calibrated, GPS ${sig}`,
    i.compass === "disturbed" ? "compass disturbed" : i.compass === "provisional" ? "compass settling" : i.compass === "none" ? "no compass" : "compass OK",
    i.vo ? `camera tracking ${i.vo}` : null,
  ].filter(Boolean);
  return { icon, text, tone, compass: i.compass, vo: i.vo, label: `${words.join(", ")}. Tap for details, Undo and Reset.` };
}

// --- calibration methods (2026-10-08, the user's verdict from the field:
// "Auto road calibration not amazing, but tapping the kerb in two spots was
// excellent") ----------------------------------------------------------------
// The two-tap kerb calibration is the primary, default path; the camera's
// automatic kerb finder stays as an optional "Try automatic" with an honest
// confidence read-out, and a low-confidence result points back to the taps.

/** The calibrate menu, in order; the first is the prominent default. */
export const CAL_METHODS = ["kerb", "drag", "lid", "auto-road"] as const;
export type CalMethod = (typeof CAL_METHODS)[number];
export const PRIMARY_CAL_METHOD: CalMethod = "kerb";

export interface AutoConfidence {
  level: "good" | "fair" | "low";
  /** 0..100, for the meter */
  pct: number;
  /** offer (prominently) the two-tap kerb calibration instead */
  suggestKerb: boolean;
  /** one honest sentence for the preview sheet */
  text: string;
}

/** How far to trust an automatic road / kerb alignment. `confidence` is the
 * server's 0..1; `uncertaintyDeg` its heading spread; `warning` any caveat it
 * returned; `found` = it found a line at all. */
export function autoRoadConfidence(confidence: number | null | undefined,
  opts: { warning?: string | null; uncertaintyDeg?: number | null; found?: boolean } = {}): AutoConfidence {
  if (opts.found === false || confidence === null || confidence === undefined || !Number.isFinite(confidence)) {
    return { level: "low", pct: 0, suggestKerb: true,
      text: "Automatic couldn't find the kerb here. Tapping the kerb in two places is more reliable." };
  }
  const c = Math.max(0, Math.min(1, confidence));
  const pct = Math.round(c * 100);
  const wide = (opts.uncertaintyDeg ?? 0) > 8;
  const level: AutoConfidence["level"] = c >= 0.7 && !wide ? "good" : c >= 0.45 ? "fair" : "low";
  const suggestKerb = level === "low" || (level === "fair" && (!!opts.warning || wide));
  const text = level === "good"
    ? `Confidence ${pct}%: good. Check the yellow kerb lines sit on the real kerbs before applying.`
    : level === "fair"
      ? `Confidence ${pct}%: fair - it may be a little off.${suggestKerb ? " Tapping the kerb in two places is more reliable here." : ""}`
      : `Confidence ${pct}%: low - this is likely to be wrong. Tap the kerb in two places instead.`;
  return { level, pct, suggestKerb, text };
}
